import { useState, useEffect } from 'react';
import { sanityFetch } from '../lib/sanity';
import { regionQueries, areaQueries } from '../lib/sanityQueries';

// Custom hook to replace static region/area data with Sanity data
export const useRegionsData = () => {
  const [regionsData, setRegionsData] = useState(null);
  const [areasData, setAreasData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch regions and areas in parallel
        const [regions, areas] = await Promise.all([
          sanityFetch(regionQueries.getAllRegions),
          sanityFetch(areaQueries.getAllAreas)
        ]);

        setRegionsData(regions);
        setAreasData(areas);
      } catch (err) {
        console.error('Error fetching regions/areas data:', err);
        setError(err);
        setRegionsData([]);
        setAreasData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Create REGION_BLURB-compatible object from Sanity data
  const regionBlurb = regionsData?.reduce((acc, region) => {
    if (region.title) {
      acc[region.title] = region.oneLiner || region.shortDescription;
    }
    return acc;
  }, {}) || {};

  // Create AREA_BLURB-compatible object from Sanity data
  const areaBlurb = areasData?.reduce((acc, area) => {
    if (area.title) {
      acc[area.title] = area.oneLiner;
      acc[area.title.toLowerCase()] = area.oneLiner;
    }
    // Also add mapping using slug (which matches areas.json name field)
    if (area.slug?.current) {
      acc[area.slug.current] = area.oneLiner;
    }
    return acc;
  }, {}) || {};

  // Transform regions data to match existing component expectations
  const regions = regionsData?.map(region => region.title).filter(Boolean) || [];

  return {
    // Data arrays
    regions,
    regionsData,
    areasData,
    
    // Backward-compatible objects for easy migration
    regionBlurb,
    areaBlurb,
    
    // Loading states
    loading,
    error
  };
};

// Separate hook for just region blurb data (lighter weight)
export const useRegionBlurb = () => {
  const { regionBlurb, areaBlurb, loading } = useRegionsData();
  return { regionBlurb, areaBlurb, loading };
};
